import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TICKET_TYPES = [
  { value: 'chamada_agendada', label: 'Novo Pedido de Chamada Agendada' },
  { value: 'chamada_imediata', label: 'Novo Pedido de Chamada Imediata' },
  { value: 'tratamento_pendencia', label: 'Tratamento Pendencia' },
  { value: 'pedido_anulacao', label: 'Pedido Anulacao' },
];

const PRIORITIES = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

export const CreateTicketModal = ({ open, onOpenChange, contract, onCreated }) => {
  const { user } = useAuth();
  const [userDetail, setUserDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [formData, setFormData] = useState({
    tipo: '',
    description: '',
    priority: 'normal',
    agendamento: '',
    motivo_anulacao: '',
  });

  useEffect(() => {
    if (open) {
      fetchUserDetail();
      setFormData({
        tipo: '',
        description: '',
        priority: 'normal',
        agendamento: '',
        motivo_anulacao: '',
      });
      setPendingFiles([]);
    }
  }, [open]);

  const fetchUserDetail = async () => {
    try {
      const [meRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/auth/me`),
        axios.get(`${API_URL}/teams`)
      ]);
      const me = meRes.data;
      const allTeams = teamsRes.data;
      const userTeam = allTeams.find(t => t.id === me.team_id);
      setUserDetail({
        ...me,
        team_name: userTeam?.name || me.partner_name || 'Equipa Interna'
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setPendingFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipo) {
      toast.error('Tipo e obrigatorio');
      return;
    }

    setSaving(true);
    try {
      const teamIds = userDetail?.team_id ? [userDetail.team_id] : [];
      const payload = {
        ...formData,
        contract_id: contract?.id || null,
        assigned_to: user?.id || null,
        team_ids: teamIds,
      };
      // Clear motivo_anulacao if not pedido_anulacao
      if (formData.tipo !== 'pedido_anulacao') {
        payload.motivo_anulacao = null;
      }

      const res = await axios.post(`${API_URL}/tickets`, payload);
      const ticketId = res.data.id;

      // Upload pending files (axios sets Content-Type with boundary automatically — DO NOT override)
      const okFiles = [];
      const failedFiles = [];
      for (const file of pendingFiles) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('ticket_id', ticketId);
        try {
          const upRes = await axios.post(`${API_URL}/files/upload`, fd);
          okFiles.push({ id: upRes.data.id, name: file.name });
        } catch {
          failedFiles.push(file.name);
        }
      }

      // System comment in the thread reporting how many files were attached
      if (okFiles.length > 0) {
        try {
          const sysFd = new FormData();
          sysFd.append('content', `📎 ${okFiles.length} ficheiro${okFiles.length !== 1 ? 's' : ''} anexado${okFiles.length !== 1 ? 's' : ''} ao criar o ticket: ${okFiles.map(f => f.name).join(', ')}`);
          sysFd.append('is_system', 'true');
          await axios.post(`${API_URL}/tickets/${ticketId}/comments`, sysFd);
        } catch {
          // non-fatal
        }
      }

      // Feedback toasts
      if (failedFiles.length === 0 && okFiles.length === 0) {
        toast.success('Ticket criado');
      } else if (failedFiles.length === 0) {
        toast.success(`Ticket criado · ${okFiles.length} ficheiro${okFiles.length !== 1 ? 's' : ''} OK`);
      } else if (okFiles.length === 0) {
        toast.error(`Ticket criado mas 0/${pendingFiles.length} ficheiros foram carregados (verifica tamanho e tipo)`);
      } else {
        toast.warning(`Ticket criado · ${okFiles.length} de ${pendingFiles.length} ficheiros OK (falhou: ${failedFiles.join(', ')})`);
      }

      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar ticket');
    } finally {
      setSaving(false);
    }
  };

  const companyName = contract?.company_name || contract?.company?.toUpperCase() || '';
  const userName = user?.name || '';
  const teamName = userDetail?.team_name || 'Equipa Interna';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-ticket-modal">
        <DialogHeader className="bg-[#00B4D8] -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
          <DialogTitle className="text-white font-outfit text-lg">Criar Ticket</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-4">
          {/* Row 1: Tipo + Contract info + Assigned user */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Tipo *</Label>
              <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="ticket-tipo-select">
                  <SelectValue placeholder="Selecionar" />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-500">{companyName}</Label>
              <p className="mt-1.5 text-sm font-medium text-[#00B4D8]">
                {contract?.nome_cliente || '-'}
              </p>
            </div>

            <div>
              <Label>Utilizador atribuido *</Label>
              <div className="mt-1.5 px-3 py-2 bg-slate-100 rounded-md border text-sm text-slate-700" data-testid="ticket-assigned-display">
                {userName}
              </div>
            </div>
          </div>

          {/* Row 2: Estado + Prioridade + Equipas */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Estado</Label>
              <div className="mt-1.5 px-3 py-2 bg-slate-50 rounded-md border text-sm text-green-600 font-medium">
                Novo
              </div>
            </div>

            <div>
              <Label>Prioridade</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="ticket-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Equipas</Label>
              <div className="mt-1.5 px-3 py-2 bg-slate-100 rounded-md border text-sm text-slate-700" data-testid="ticket-team-display">
                {teamName}
              </div>
            </div>
          </div>

          {/* Row 3: Agendamento + Motivo Anulacao (conditional) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Agendamento</Label>
              <Input
                type="datetime-local"
                value={formData.agendamento}
                onChange={(e) => setFormData({ ...formData, agendamento: e.target.value })}
                className="mt-1.5 bg-slate-50"
                data-testid="ticket-agendamento-input"
              />
            </div>
            {formData.tipo === 'pedido_anulacao' && (
              <div>
                <Label>Motivo da Anulacao</Label>
                <Textarea
                  value={formData.motivo_anulacao}
                  onChange={(e) => setFormData({ ...formData, motivo_anulacao: e.target.value })}
                  placeholder=""
                  className="mt-1.5 bg-slate-50 min-h-[80px]"
                  data-testid="ticket-motivo-input"
                />
              </div>
            )}
          </div>

          {/* Descricao */}
          <div>
            <Label>Descricao</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder=""
              className="mt-1.5 bg-slate-50 min-h-[100px]"
              data-testid="ticket-description-input"
            />
          </div>

          {/* Anexos */}
          <div>
            <Label>Anexos</Label>
            <div className="mt-1.5 space-y-2">
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 px-3 py-2 rounded border">
                  <Paperclip className="w-4 h-4 text-slate-400" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)} className="text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#00B4D8] transition-colors">
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  data-testid="ticket-file-input"
                />
                <Paperclip className="w-5 h-5" />
                Adicionar ficheiro
              </label>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] rounded-full px-6"
              data-testid="ticket-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {saving ? 'A guardar...' : 'Guardar'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
